import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import 'bootstrap/dist/css/bootstrap.min.css';
import { setAuth } from '../Components/Redux/authSlice';
import { RootState } from '../Components/Redux/store';
import { FaExclamationTriangle, FaFileExport, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import './fees.css';

interface Student {
  id: number;
  name: string;
  class_name: string;
  guardian_number: string;
  address: string;
}

// Helper function to get cookie
const getCookie = (name: string) => {
  const cookieName = name + "=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');
  for (let i = 0; i < cookieArray.length; i++) {
    let cookie = cookieArray[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(cookieName) === 0) {
      return cookie.substring(cookieName.length, cookie.length);
    }
  }
  return '';
};

const FeesNotPaid: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [month, setMonth] = useState<string>((new Date().getMonth() + 1).toString());
  const [year] = useState<number>(new Date().getFullYear());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }

        setLoading(true);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get(`/fees/fees_not_paid/?month=${month}&year=${year}`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        console.log('Fetched students who have not paid:', response.data);
        setStudents(response.data || []);
        setLoading(false);
        dispatch(setAuth(true));
      } catch (error) {
        console.error('Error fetching students:', error);
        setError('Ku guuldareystay in la soo qaado xogta ardayga');
        setLoading(false);
      }
    };

    fetchData();
  }, [month, year, dispatch]);

  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setMonth(event.target.value);
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Class', 'Guardian Number', 'Address'];
    const csvData = students.map(student => [
      student.name,
      student.class_name,
      student.guardian_number,
      student.address
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const monthName = new Date(0, parseInt(month) - 1).toLocaleString('default', { month: 'long' });
    a.download = `unpaid-fees-${monthName}-${year}.csv`;
    a.click();
  };

  return (
    <div className="dashboard-content">
      {auth ? (
        <div className="fees-container">
          {/* Header */}
          <div className="fees-header">
            <div className="header-content">
              <div className="header-title">
                <FaExclamationTriangle className="header-icon" />
                <div>
                  <h2>Warbixinta Lacagaha Aan La Bixin</h2>
                  <p className="header-subtitle">Ardayda leh lacagaha aan la bixin</p>
                </div>
              </div>
              <div className="header-actions">
                <button onClick={exportToCSV} className="btn-secondary-modern">
                  <FaFileExport /> Dhoofi Warbixin
                </button>
              </div>
            </div>
          </div>

          {/* Stats Card */}
          <div className="stats-grid">
            <div className="stat-card stat-card-orange">
              <div className="stat-icon-wrapper">
                <FaExclamationTriangle className="stat-icon" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Wadarta Aan La Bixin</p>
                <h3 className="stat-value">{students.length}</h3>
                <span className="stat-trend negative">Ardayda harsan</span>
              </div>
            </div>

            <div className="stat-card stat-card-blue">
              <div className="stat-icon-wrapper">
                <FaExclamationTriangle className="stat-icon" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Bisha La Doortay</p>
                <h3 className="stat-value" style={{ fontSize: '1.5rem' }}>
                  {new Date(0, parseInt(month) - 1).toLocaleString('default', { month: 'long' })}
                </h3>
                <span className="stat-trend">{year}</span>
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="filters-section">
            <div className="filter-group">
              <div className="month-selector-group">
                <label htmlFor="monthSelect" className="month-label">
                  Ku Shaandhee Bisha:
                </label>
                <select
                  id="monthSelect"
                  className="month-filter"
                  value={month}
                  onChange={handleMonthChange}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>
                      {new Date(0, m - 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Table Container */}
          <div className="table-container">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Soo dejinaya lacagaha aan la bixin...</p>
              </div>
            ) : error ? (
              <div className="alert-error">
                <FaExclamationTriangle />
                <span>{error}</span>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Magaca Ardayga</th>
                      <th>Fasalka</th>
                      <th>Nambarka Waalidka</th>
                      <th>Cinwaanka</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="empty-state">
                          <FaExclamationTriangle />
                          <p>Dhamaan lacagaha waa la bixiyay bisha {new Date(0, parseInt(month) - 1).toLocaleString('default', { month: 'long' })}!</p>
                          <small style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                            Shaqo fiican ayaad ka qabatay ururinta lacagaha! 🎉
                          </small>
                        </td>
                      </tr>
                    ) : (
                      students.map((student, index) => (
                        <tr key={student.id} className="table-row">
                          <td>{index + 1}</td>
                          <td className="student-name">{student.name}</td>
                          <td>
                            <span className="class-badge">{student.class_name}</span>
                          </td>
                          <td>
                            <div className="contact-cell">
                              <FaPhone className="contact-icon" />
                              <span>{student.guardian_number || 'Eber'}</span>
                            </div>
                          </td>
                          <td>
                            <div className="contact-cell">
                              <FaMapMarkerAlt className="contact-icon" />
                              <span>{student.address || 'Eber'}</span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="auth-error">
          <h4>Ma aadan xaqiijin</h4>
          <p>Fadlan soo gal si aad u aragto faahfaahinta lacag bixinta</p>
        </div>
      )}
    </div>
  );
};

export default FeesNotPaid;